import { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CarCard from "./CarCard";

/**
 * Horizontal scrolling carousel for one homepage category section.
 *
 * Behavior:
 *   - Snap-x scroll on mobile (native touch flick), drag-to-scroll on desktop
 *     via overflow-x-auto. No JS scroll-on-touch handler — the browser does
 *     it better than any custom implementation.
 *   - Desktop: chevron buttons appear on hover. They scroll by one card width.
 *   - Cards lazy-load images via the browser's native `loading="lazy"` — set
 *     on the <img> inside CarCard's image (already present).
 *   - First card is left-padded to align with the page container; trailing
 *     spacer ensures the last card can scroll fully into view past the edge.
 *
 * Each section is an anchor (`#category-{id}`) so the CategoryNav chip strip
 * can `scrollIntoView()` directly to it.
 */
export default function CategoryCarousel({ id, title, subtitle, items, onSeeAll }) {
  const { t } = useTranslation();
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateButtons = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    updateButtons();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, [updateButtons, items]);

  const scrollByStep = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-item]");
    const step = card ? card.getBoundingClientRect().width + 16 : 320;
    el.scrollBy({ left: dir * step * 2, behavior: "smooth" });
  };

  if (!items || items.length === 0) return null;

  return (
    <section
      id={`category-${id}`}
      className="py-10 scroll-mt-32"
      data-testid={`category-section-${id}`}
    >
      <div className="container-main">
        <div className="flex items-end justify-between mb-5 px-1">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-heading font-bold text-[#111111] leading-tight truncate">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-neutral-500 mt-0.5">{subtitle}</p>
            )}
          </div>
          {onSeeAll && (
            <button
              type="button"
              onClick={onSeeAll}
              data-testid={`category-see-all-${id}`}
              className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#111111] hover:underline"
            >
              {t("carousel.seeAll")}
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="relative group">
        {/* Desktop arrows — hidden on touch, fade in on hover */}
        {canLeft && (
          <button
            type="button"
            onClick={() => scrollByStep(-1)}
            aria-label={t("carousel.prev")}
            data-testid={`category-prev-${id}`}
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-neutral-200 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-neutral-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {canRight && (
          <button
            type="button"
            onClick={() => scrollByStep(1)}
            aria-label={t("carousel.next")}
            data-testid={`category-next-${id}`}
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-neutral-200 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-neutral-50"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        <div
          ref={scrollerRef}
          className="
            flex gap-4 overflow-x-auto snap-x snap-mandatory
            scroll-px-4 sm:scroll-px-8
            px-4 sm:px-8
            pb-4
            [-ms-overflow-style:none] [scrollbar-width:none]
            [&::-webkit-scrollbar]:hidden
          "
          data-testid={`category-scroll-${id}`}
        >
          {items.map((v) => (
            <div
              key={v.vehicle_id}
              data-carousel-item
              className="snap-start shrink-0 w-[78%] sm:w-[44%] md:w-[32%] lg:w-[24%] xl:w-[19%]"
            >
              <CarCard vehicle={v} />
            </div>
          ))}
          {/* Trailing spacer so the final card's right edge reaches the page edge */}
          <div className="shrink-0 w-2" aria-hidden />
        </div>
      </div>
    </section>
  );
}
